/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { localZone } from '@/lib/clock';
import { MONTHS, formatDate } from '@/lib/dates';

/**
 * Which days the dashboard reports on.
 *
 * What a person keeps is the choice, not the dates. "Last 7 days" saved on a
 * Monday means the seven days to *today* when the dashboard is opened on
 * Friday; the pair of dates it stood for on Monday would have gone stale by
 * lunchtime. So the dates are worked out here, each time, from today where
 * the reader is, and sent to the server, which answers for exactly those days
 * and never has to guess what "last week" meant to somebody in another zone.
 *
 * Every range is inclusive at both ends, and every date is a calendar date
 * written `YYYY-MM-DD`, held as midnight UTC while it is being worked on so
 * that no local offset can move it across a day.
 */

/** Every choice the picker offers, in the order it offers them. */
export const PRESET_KEYS = [
  'last_7_days',
  'last_30_days',
  'last_3_months',
  'last_6_months',
  'this_quarter',
  'this_year',
  'last_12_months',
  'custom',
] as const;

export type RangePreset = (typeof PRESET_KEYS)[number];

/** A choice as it is saved: a preset, or two dates of the person's own. */
export interface RangeChoice {
  preset: RangePreset;
  from?: string | undefined;
  to?: string | undefined;
}

/** The days a choice stands for today: first and last, both included. */
export interface DateSpan {
  from: string;
  to: string;
}

/** Where the dashboard opens for somebody who has never chosen. */
export const DEFAULT_RANGE: RangeChoice = { preset: 'this_year' };

export const PRESET_LABEL: Record<RangePreset, string> = {
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  last_3_months: 'Last 3 months',
  last_6_months: 'Last 6 months',
  this_quarter: 'This quarter',
  this_year: 'This year',
  last_12_months: 'Last 12 months',
  custom: 'Custom range',
};

/** The longest range the server will answer for. */
export const MOST_YEARS = 10;

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * A `YYYY-MM-DD` string as a date, or null when it is not one.
 *
 * `2026-02-31` is shaped like a date and `Date.UTC` would quietly roll it on
 * to the third of March, so a value that does not come back the same way it
 * went in was never a date.
 */
function toDate(iso: string): Date | null {
  const match = ISO.exec(iso);
  if (!match) return null;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return toIso(date) === iso ? date : null;
}

function shiftDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

/**
 * The same day some months away, held to the end of a shorter month.
 *
 * Three months before 31 May is 28 February, not the third of March: a range
 * that asked for three months and got two and a bit would be wrong in a way
 * nobody would notice.
 */
function shiftMonths(date: Date, months: number): Date {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + months;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(date.getUTCDate(), last)));
}

/**
 * Today where the reader is, as `YYYY-MM-DD`.
 *
 * Read in the zone, like the greeting and the week: near midnight the
 * machine's own day can be the one either side of the reader's.
 */
export function todayIn(now: Date = new Date(), zone: string = localZone()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * What is wrong with two dates as a range, in a sentence, or null.
 *
 * Refused here as the server would refuse them, so the person hears about it
 * beside the dates they typed rather than from a dashboard that will not load.
 * Ten years is counted as the server counts it: the end must fall before the
 * same date ten years on, 29 February landing on the 28th.
 */
export function problemWith(from: string, to: string): string | null {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 'Choose a date for each end of the range.';
  if (start.getTime() > end.getTime()) {
    return 'The range ends before it starts. Choose an end on or after the start.';
  }
  if (end.getTime() >= shiftMonths(start, 12 * MOST_YEARS).getTime()) {
    return `A range can cover ${String(MOST_YEARS)} years at most.`;
  }
  return null;
}

/**
 * A saved choice this build can act on.
 *
 * A range of the person's own that has lost a date, or that no longer makes
 * sense, opens as the default rather than as a dashboard the server refuses.
 */
export function settled(choice: RangeChoice): RangeChoice {
  if (choice.preset !== 'custom') return { preset: choice.preset };
  const { from, to } = choice;
  if (from === undefined || to === undefined || problemWith(from, to) !== null) {
    return DEFAULT_RANGE;
  }
  return { preset: 'custom', from, to };
}

/**
 * The days a choice stands for, counted from today where the reader is.
 *
 * `day` is today as a `YYYY-MM-DD` date rather than an instant, so the
 * caller decides when the day has turned.
 *
 * "Last" ranges end today and include it: the last seven days are today and
 * the six before. A month-long one runs from the day after the same date that
 * many months back, so "Last 3 months" on 28 September begins on 29 June.
 * "This quarter" and "This year" are the whole of the quarter or the year,
 * the days still to come included, so the chart reads Q1 to Q4 whatever the
 * date and marks what has not happened yet as not yet.
 */
export function resolveRange(choice: RangeChoice, day: string = todayIn()): DateSpan {
  const today = toDate(day) ?? new Date();
  const year = today.getUTCFullYear();
  const span = (from: Date, to: Date): DateSpan => ({ from: toIso(from), to: toIso(to) });
  const monthsBack = (months: number) =>
    span(shiftDays(shiftMonths(today, -months), 1), today);
  const thisYear = span(new Date(Date.UTC(year, 0, 1)), new Date(Date.UTC(year, 11, 31)));

  switch (choice.preset) {
    case 'last_7_days':
      return span(shiftDays(today, -6), today);
    case 'last_30_days':
      return span(shiftDays(today, -29), today);
    case 'last_3_months':
      return monthsBack(3);
    case 'last_6_months':
      return monthsBack(6);
    case 'last_12_months':
      return monthsBack(12);
    case 'this_quarter': {
      const first = Math.floor(today.getUTCMonth() / 3) * 3;
      return span(
        new Date(Date.UTC(year, first, 1)),
        // Day nought of the month after is the last day of this one.
        new Date(Date.UTC(year, first + 3, 0)),
      );
    }
    case 'custom': {
      const kept = settled(choice);
      return kept.from !== undefined && kept.to !== undefined
        ? { from: kept.from, to: kept.to }
        : thisYear;
    }
    case 'this_year':
      return thisYear;
  }
}

/**
 * Two dates as a person writes a span of them.
 *
 * The year is said once when both ends share it, and the month once when
 * both ends share that too: `12 Mar – 28 Sep 2026`, `22–28 Sep 2026`.
 */
export function formatSpan(from: string, to: string): string {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from === to) return formatDate(to);

  const day = String(start.getUTCDate());
  const month = MONTHS[start.getUTCMonth()] ?? '';
  if (start.getUTCFullYear() !== end.getUTCFullYear()) {
    return `${formatDate(from)} – ${formatDate(to)}`;
  }
  if (start.getUTCMonth() !== end.getUTCMonth()) {
    return `${day} ${month} – ${formatDate(to)}`;
  }
  // Closed up within one month, as the server writes it: `22–28 Sep 2026`.
  return `${day}–${formatDate(to)}`;
}

/** What the picker's chip says: the preset's name, or the dates themselves. */
export function rangeName(choice: RangeChoice, span: DateSpan): string {
  return choice.preset === 'custom'
    ? formatSpan(span.from, span.to)
    : PRESET_LABEL[choice.preset];
}

/**
 * The range in full, for a heading: the preset's name and the dates it
 * stands for today, because "Last 3 months" alone leaves the reader to work
 * out where it starts.
 */
export function rangeInFull(choice: RangeChoice, span: DateSpan): string {
  const dates = formatSpan(span.from, span.to);
  return choice.preset === 'custom' ? dates : `${PRESET_LABEL[choice.preset]} · ${dates}`;
}
